import { ReactElement } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MenuItem, Typography } from '@mui/material';
import SelectField from 'shared/components/formFields/select-field';
import ViewingModeController from 'shared/components/viewing-mode-controller/viewing-mode-controller';
import { DeviceType, isG2Device } from 'shared/models/device-type';

import { useDeviceDetailsHook } from 'features/device/device-details/hooks/device-details-hook';

export const DeviceMonitoringConstants = {
  OneHourValue: 60,
  TwelveHoursValue: 720,
  TwentyFourHoursValue: 1440,
  SeventyTwoHoursValue: 4320,
};

function DeviceMonitoring(): ReactElement {
  const { t } = useTranslation('deviceDetails');

  const { currentViewingMode, currentDevice, getDefaultExpectedRecurrence, currentDeviceType } = useDeviceDetailsHook();
  const { control } = useFormContext();

  const deviceMonitoringOptions = [
    <MenuItem data-testid={`device-monitoring-one-hour`} key={`device-monitoring-one-hour`} value={DeviceMonitoringConstants.OneHourValue}>
      {`${DeviceMonitoringConstants.OneHourValue / 60} ${t('deviceMonitoringOptions.hours')}`}
    </MenuItem>,
    <MenuItem
      data-testid={`device-monitoring-twelve-hours`}
      key={`device-monitoring-twelve-hours`}
      value={DeviceMonitoringConstants.TwelveHoursValue}
    >
      {`${DeviceMonitoringConstants.TwelveHoursValue / 60} ${t('deviceMonitoringOptions.hours')}`}
    </MenuItem>,
    <MenuItem
      data-testid={`device-monitoring-twenty-four-hours`}
      key={`device-monitoring-twenty-four-hours`}
      value={DeviceMonitoringConstants.TwentyFourHoursValue}
    >
      {`${DeviceMonitoringConstants.TwentyFourHoursValue / 60} ${t('deviceMonitoringOptions.hours')}`}
    </MenuItem>,
  ];

  const G2DeviceMonitoringOptions = [
    <MenuItem
      data-testid={`device-monitoring-seventy-two-hours`}
      key={`device-monitoring-seventy-two-hours`}
      value={DeviceMonitoringConstants.SeventyTwoHoursValue}
    >
      {`${DeviceMonitoringConstants.SeventyTwoHoursValue / 60} ${t('deviceMonitoringOptions.hours')}`}
    </MenuItem>,
  ];

  const getMonitoringOptionsByDeviceType = (currentDeviceType?: DeviceType) => {
    switch (currentDeviceType) {
      case DeviceType.G2Transmitter:
      case DeviceType.G2SmokeAlarm:
      case DeviceType.G2MotionDetector:
      case DeviceType.G2DoorContact:
      case DeviceType.G2TableAlarm:
      case DeviceType.G2Contact:
      case DeviceType.G2ToiletAlarm:
        return G2DeviceMonitoringOptions;
      default:
        return deviceMonitoringOptions;
    }
  };

  return (
    <>
      {currentDevice?.deviceMonitoring?.expectedRecurrence && currentViewingMode === 'viewing' && (
        <Typography variant="subtitle1">
          {t('deviceMonitoringOptions.description', {
            hours: currentDevice.deviceMonitoring.expectedRecurrence / 60,
          })}
        </Typography>
      )}

      {(currentViewingMode === 'creation' || currentViewingMode === 'editing') && (
        <ViewingModeController
          viewingMode={currentViewingMode}
          name={`deviceMonitoring.expectedRecurrence`}
          control={control}
          label={t('deviceMonitoringOptions.label')}
          render={({ field }) => (
            <SelectField
              {...field}
              inputProps={{ 'data-testid': 'MonitoringExpectedRecurrence' }}
              id="deviceMonitoring.expectedRecurrence"
              variant="outlined"
              label={t('deviceMonitoringOptions.label')}
              disabled={currentDeviceType ? isG2Device(currentDeviceType) : false}
              className="select-field"
              value={`${field.value ?? getDefaultExpectedRecurrence()}`}
            >
              {getMonitoringOptionsByDeviceType(currentDeviceType)}
            </SelectField>
          )}
        />
      )}
    </>
  );
}

export default DeviceMonitoring;
